import React, { useState, useEffect } from 'react';
import { X, FileText, Layout, DollarSign, Calendar, Gift, Search, Trash2, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminFetch } from '@/utils/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  preset: <Layout className="w-5 h-5" />,
  landing: <FileText className="w-5 h-5" />,
  pricing: <DollarSign className="w-5 h-5" />,
  event: <Calendar className="w-5 h-5" />,
  gift: <Gift className="w-5 h-5" />,
  custom: <Plus className="w-5 h-5" />
};

const TemplatePicker = ({ isOpen, onClose, onSelectTemplate, currentBlocks = [] }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      const response = await adminFetch('/admin/templates');
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    // Generate new unique IDs for each block
    const blocksWithNewIds = template.blocks.map(block => ({
      ...block,
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }));
    onSelectTemplate(blocksWithNewIds);
    onClose();
    toast.success(`Template "${template.name}" applied!`);
  };

  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    if (currentBlocks.length === 0) {
      toast.error('Cannot save empty page as template');
      return;
    }

    try {
      const response = await adminFetch('/admin/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTemplateName,
          description: newTemplateDesc,
          category: 'custom',
          blocks: currentBlocks,
          thumbnail: ''
        })
      });

      if (response.ok) {
        toast.success('Template saved successfully!');
        setShowSaveModal(false);
        setNewTemplateName('');
        setNewTemplateDesc('');
        loadTemplates();
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (e, templateId) => {
    e.stopPropagation();
    
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await adminFetch(`/admin/templates/${templateId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Template deleted');
        loadTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || 
                           (filterCategory === 'preset' && template.is_preset) ||
                           (filterCategory === 'custom' && !template.is_preset);
    return matchesSearch && matchesCategory;
  });

  const presetTemplates = filteredTemplates.filter(t => t.is_preset);
  const customTemplates = filteredTemplates.filter(t => !t.is_preset);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-zinc-900 rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Page Templates</h2>
              <p className="text-sm text-zinc-400">Start with a pre-built layout or use your saved templates</p>
            </div>
            <div className="flex items-center gap-2">
              {currentBlocks.length > 0 && (
                <button
                  onClick={() => setShowSaveModal(true)}
                  className="px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Save Current as Template
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="p-4 border-b border-zinc-800 flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Templates</option>
              <option value="preset">Preset Templates</option>
              <option value="custom">My Templates</option>
            </select>
          </div>

          {/* Template Grid */}
          <div className="p-4 overflow-y-auto max-h-[55vh]">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading templates...</div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                No templates found. Create your first page and save it as a template!
              </div>
            ) : (
              <div className="space-y-6">
                {/* Preset Templates */}
                {presetTemplates.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3">Starter Templates</h3>
                    <div className="grid grid-cols-3 gap-4">
                      {presetTemplates.map(template => (
                        <button
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                          className="group p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 hover:border-primary/50 rounded-xl text-left transition-all"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="p-2 bg-primary/20 rounded-lg text-primary">
                              {categoryIcons[template.category] || categoryIcons.preset}
                            </div>
                            <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                              Preset
                            </span>
                          </div>
                          <h4 className="font-bold text-white mb-1">{template.name}</h4>
                          <p className="text-sm text-zinc-400 line-clamp-2">{template.description}</p>
                          <div className="mt-3 text-xs text-zinc-500">
                            {template.blocks?.length || 0} blocks
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Templates */}
                {customTemplates.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3">My Templates</h3>
                    <div className="grid grid-cols-3 gap-4">
                      {customTemplates.map(template => (
                        <button
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                          className="group p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 hover:border-primary/50 rounded-xl text-left transition-all relative"
                        >
                          <button
                            onClick={(e) => handleDeleteTemplate(e, template.id)}
                            className="absolute top-2 right-2 p-1.5 bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <div className="flex items-start mb-2">
                            <div className="p-2 bg-zinc-700 rounded-lg text-zinc-400">
                              {categoryIcons.custom}
                            </div>
                          </div>
                          <h4 className="font-bold text-white mb-1">{template.name}</h4>
                          <p className="text-sm text-zinc-400 line-clamp-2">{template.description || 'No description'}</p>
                          <div className="mt-3 text-xs text-zinc-500">
                            {template.blocks?.length || 0} blocks
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save Template Modal */}
          <AnimatePresence>
            {showSaveModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 flex items-center justify-center"
                onClick={() => setShowSaveModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.9 }}
                  className="bg-zinc-900 p-6 rounded-xl border border-zinc-700 w-full max-w-md"
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-lg font-bold text-white mb-4">Save as Template</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-1">Template Name</label>
                      <input
                        type="text"
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        placeholder="My Custom Template"
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-1">Description (optional)</label>
                      <textarea
                        value={newTemplateDesc}
                        onChange={(e) => setNewTemplateDesc(e.target.value)}
                        placeholder="Describe what this template is for..."
                        rows={3}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div className="text-sm text-zinc-500">
                      This will save your current page layout ({currentBlocks.length} blocks) as a reusable template.
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowSaveModal(false)}
                      className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAsTemplate}
                      className="flex-1 px-4 py-2 bg-primary hover:bg-primary/90 text-black font-bold rounded-lg"
                    >
                      Save Template
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TemplatePicker;
